'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLang } from '@/lib/langContext';
import { useAuth } from '@/contexts/AuthContext';

interface Props {
  onBack: () => void;
  email?: string;
}

export default function VerifyEmailState({ onBack, email }: Props) {
  const { t, dir } = useLang();
  const { resendVerificationEmail } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResend = async () => {
    if (!email) return;
    setIsResending(true);
    setResendError(null);
    setResendSuccess(false);
    try {
      await resendVerificationEmail(email);
      setResendSuccess(true);
    } catch (error: any) {
      setResendError(
        dir === 'rtl' ?'فشل إعادة الإرسال، حاول مرة أخرى' :'Failed to resend. Please try again.'
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="fade-in text-center">
      <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
        <Icon name="EnvelopeIcon" size={40} className="text-primary" />
      </div>
      <h2 className="text-2xl font-800 text-foreground mb-3">{t.verifyEmail}</h2>
      <p className="text-muted-foreground text-base leading-relaxed mb-2">{t.verifyEmailMsg}</p>
      {email && (
        <p className="text-primary font-600 text-sm mb-6">{email}</p>
      )}

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 text-start">
        <div className="flex items-start gap-3">
          <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-secondary-foreground">
            {dir === 'rtl' ?'تحقق من مجلد الرسائل غير المرغوب فيها إذا لم تجد الرسالة في صندوق الوارد.'
              : "Check your spam folder if you don't see the email in your inbox."}
          </p>
        </div>
      </div>

      {resendSuccess && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2 text-sm text-green-700">
          <Icon name="CheckCircleIcon" size={16} className="flex-shrink-0" />
          {dir === 'rtl' ? 'تم إرسال رسالة التحقق بنجاح' : 'Verification email sent successfully'}
        </div>
      )}

      {resendError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2 text-sm text-red-700">
          <Icon name="ExclamationCircleIcon" size={16} className="flex-shrink-0" />
          {resendError}
        </div>
      )}

      <button
        onClick={handleResend}
        disabled={isResending || !email}
        className="btn-primary w-full py-3 rounded-lg font-600 mb-3 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {isResending ? (
          <><Icon name="ArrowPathIcon" size={18} className="animate-spin" />{dir === 'rtl' ? 'جارٍ الإرسال...' : 'Sending...'}</>
        ) : t.resendEmail}
      </button>
      <button onClick={onBack} className="w-full py-3 rounded-lg font-500 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
        {dir === 'rtl' ? 'العودة لتسجيل الدخول' : 'Back to Sign In'}
      </button>
    </div>
  );
}